import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getSocket } from '../services/socket';
import webSocketEventBus from '../services/webSocketEventBus';
import api from '../api/client';
import toast from 'react-hot-toast';

// Resolve a symptomId from any notification data shape
const getSymptomId = (data) =>
  data?.symptomId || data?.symptom?._id || data?.id || null;

const NotificationSystem = () => {
  
  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem('symptoconnect_notifications');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [unreadCount, setUnreadCount] = useState(() => {
    try {
      const saved = localStorage.getItem('symptoconnect_notifications');
      const parsed = saved ? JSON.parse(saved) : [];
      return parsed.filter(n => !n.read).length;
    } catch { return 0; }
  });
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Use refs to access latest values without causing re-renders
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  const notificationsRef = useRef(notifications);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);
  

  // Add notification function
  const lastNotifRef = useRef({ key: '', time: 0 });
  const addNotification = useCallback((notification) => {
    // Deduplicate: skip if same type+title within 2 seconds
    const dedupeKey = `${notification.type}:${notification.title}`;
    const now = Date.now();
    if (dedupeKey === lastNotifRef.current.key && now - lastNotifRef.current.time < 2000) {
      return;
    }
    lastNotifRef.current = { key: dedupeKey, time: now };

    const newNotification = {
      id: now + Math.random(),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);

    // Show browser notification if permission granted
    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico'
      });
    }

    // Show in-app toast (stable id prevents duplicates)
    toast(notification.title, {
      id: dedupeKey,
      icon: '🔔',
      duration: 4000,
      style: { fontWeight: 600, fontSize: '14px' },
    });
  }, []);

  // Load persisted notifications from API — callable from WebSocket handlers too.
  // Must be declared before handleNewSymptom which depends on it.
  const loadPersistedNotifications = useCallback(async () => {
    try {
      const res = await api.get('/profile/notifications/history?limit=30');
      const serverNotifs = res?.data?.data?.notifications || [];
      const dbIds = new Set(serverNotifs.map(sn => sn._id));

      setNotifications(prev => {
        // DB is source of truth for persisted notifications.
        // Keep in-memory-only entries (e.g. chat messages with numeric ids) not yet in DB.
        const inMemoryOnly = prev.filter(n => typeof n.id === 'number' && !dbIds.has(String(n.id)));

        const dbMapped = serverNotifs.map(sn => ({
          id: sn._id,
          type: sn.type,
          title: sn.title,
          message: sn.message,
          timestamp: sn.createdAt,
          read: sn.read,
          data: sn.data || {}
        }));

        const merged = [...dbMapped, ...inMemoryOnly];
        merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        const trimmed = merged.slice(0, 50);
        setUnreadCount(trimmed.filter(n => !n.read).length);
        return trimmed;
      });
    } catch (err) {
      console.warn('Could not load persisted notifications:', err.message);
    }
  }, []);

  // NEW SYMPTOM - When patient posts new symptom
  // Only show a toast + browser notification for real-time feedback.
  // The DB notification (from Notification.insertMany on the backend) is the source of truth
  // and gets loaded via loadPersistedNotifications — no in-memory duplicate.
  const handleNewSymptom = useCallback((data) => {
    const currentUser = userRef.current;
    if (!currentUser || currentUser.role !== 'DOCTOR') return;

    const toastMsg = data.patient?.name
      ? `New report from ${data.patient.name}: ${data.title}`
      : `New patient report: ${data.title}`;

    if (Notification.permission === 'granted') {
      new Notification('New Patient Report', { body: toastMsg, icon: '/favicon.ico' });
    }

    toast(toastMsg, {
      icon: '🔔',
      duration: 5000,
      style: { fontWeight: 600, fontSize: '14px' },
    });

    // Forward to event bus so DoctorDashboard (and AppLayout badge) can react
    webSocketEventBus.emit('newSymptom', data);

    // Refresh the notification inbox from DB so the persisted entry appears
    loadPersistedNotifications();
  }, [loadPersistedNotifications]);

  // NEW RESPONSE - When doctor responds to patient symptom
  const handleNewResponse = useCallback((data) => {
    
    // NotificationSystem handles notifications, RealDashboard handles data updates
    
    const patientId = String(data.patientId);
    const doctorId = String(data.doctorId);
    const userId = String(user?.id);
    
    if (user?.role === 'USER' && patientId === userId) {
      // Patient gets notification about doctor response
      addNotification({
        type: 'newResponse',
        title: `💬 New Response from Doctor`,
        message: `Response to: ${data.symptomTitle}`,
        data: data
      });
    } else if (user?.role === 'DOCTOR' && doctorId !== userId) {
      // Only OTHER doctors get notification for awareness (not the responding doctor)
      addNotification({
        type: 'newResponse',
        title: `👥 New Response by Colleague`,
        message: `New response to: ${data.symptomTitle}`,
        data: data
      });
    }
  }, [user, addNotification]);

  // PATIENT REPLY - When patient replies to doctor response
  const handlePatientReply = useCallback((data) => {

    // NotificationSystem handles notifications, RealDashboard handles data updates

    const doctorId = String(data.doctorId);
    const userId = String(user?.id);
    
    // Only notify the doctor (not the patient who sent the reply)
    if (user?.role === 'DOCTOR' && doctorId === userId) {
      addNotification({
        type: 'patientReply',
        title: `💬 Patient Reply`,
        message: `${data.patientName} replied to your response on: ${data.symptomTitle}`,
        data: data
      });
    }
  }, [user, addNotification]);

  // NEW CHAT MESSAGE - When someone sends a message in private chat
  const handleNewChatMessage = useCallback((data) => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    // Only notify the recipient (not the sender)
    if (String(data.recipientId) === String(currentUser.id)) {
      addNotification({
        type: 'newChatMessage',
        title: `💬 New Chat Message`,
        message: `${data.senderName} sent you a message`,
        data: {
          symptomId: data.symptomId,
          roomId: data.roomId,
          senderId: data.senderId,
          senderName: data.senderName,
          message: data.message
        }
      });
    }
  }, [addNotification]);

  // SYMPTOM STATUS UPDATE - When a doctor changes symptom status
  const handleSymptomStatusUpdate = useCallback((data) => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    const statusText = data.status === 'CLOSED' ? '🔒 Closed' : '🔓 Reopened';
    const doctorName = data.updatedBy?.name || 'A doctor';
    const updatingDoctorId = String(data.updatedBy?.id);
    const currentUserId = String(currentUser.id);

    // Notify the patient who owns the symptom
    if (String(data.patient?.id) === currentUserId) {
      addNotification({
        type: 'symptomStatusUpdate',
        title: `${statusText} - ${data.title}`,
        message: `${doctorName} has ${data.status === 'CLOSED' ? 'closed' : 'reopened'} your symptom`,
        data: {
          symptomId: data.id,
          status: data.status,
          updatedBy: data.updatedBy,
          patientId: data.patient.id
        }
      });
    }
    // Notify OTHER doctors (not the one who made the change)
    else if (currentUser.role === 'DOCTOR' && updatingDoctorId !== currentUserId) {
      addNotification({
        type: 'symptomStatusUpdate',
        title: `${statusText} by Colleague`,
        message: `${doctorName} ${data.status === 'CLOSED' ? 'closed' : 'reopened'}: ${data.title}`,
        data: {
          symptomId: data.id,
          status: data.status,
          updatedBy: data.updatedBy,
          patientId: data.patient.id
        }
      });
    }
  }, [addNotification]);

  // ACTION DELEGATED - When another doctor delegates an action to this doctor
  const handleActionDelegated = useCallback((data) => {
    const currentUser = userRef.current;
    if (!currentUser || currentUser.role !== 'DOCTOR') return;

    const actionLabel = data.actionType?.replace(/_/g, ' ') || 'action';
    addNotification({
      type: 'actionDelegated',
      title: 'Action Delegated to You',
      message: `Dr. ${data.delegatedBy} delegated "${actionLabel}" to you${
        data.note ? ` — "${data.note}"` : ''
      }`,
      data: { actionId: data.actionId, patientId: data.patientId, priority: data.priority }
    });
  }, [addNotification]);

  // APPOINTMENT SCHEDULED - When a doctor schedules a visit for the patient
  const handleAppointmentScheduled = useCallback((data) => {
    const currentUser = userRef.current;
    if (!currentUser || currentUser.role !== 'USER') return;

    const TYPE_LABEL = {
      virtual_visit: 'Virtual Visit',
      home_visit: 'Home Visit',
      pharmacist_consult: 'Pharmacist Consult',
    };
    const label = TYPE_LABEL[data.appointmentType] || data.label || 'Appointment';
    const dateStr = data.scheduledDate
      ? new Date(data.scheduledDate).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
      : '';

    addNotification({
      type: 'appointment_scheduled',
      title: `${label} Scheduled`,
      message: `${data.doctorName} scheduled a ${label}${dateStr ? ` for ${dateStr}` : ''}.`,
      data: { careEventId: data.eventId, appointmentType: data.appointmentType, meetingLink: data.meetingLink, scheduledDate: data.scheduledDate },
    });
  }, [addNotification]);

  // CARE ACTION EXECUTED - When a doctor executes an action for the patient
  const handleCareActionExecuted = useCallback((data) => {
    const currentUser = userRef.current;
    if (!currentUser || currentUser.role !== 'USER') return;

    toast(data.title || 'Your care team took action', {
      icon: '✅',
      duration: 6000,
      style: { fontWeight: 600, fontSize: '14px' },
    });
    loadPersistedNotifications();
  }, [loadPersistedNotifications]);

  // NEW CARE REQUEST - When a doctor sends a care team request to the patient
  const handleNewCareRequest = useCallback((data) => {
    const currentUser = userRef.current;
    if (!currentUser || currentUser.role !== 'USER') return;

    addNotification({
      type: 'newCareRequest',
      title: `New Care Team Request`,
      message: `Dr. ${data.doctorName}${data.specialty ? ` (${data.specialty})` : ''} wants to join your care team`,
      data: { requestId: data.requestId, doctorName: data.doctorName, doctorId: data.doctorId }
    });
  }, [addNotification]);

  // Handle notification click
  const handleNotificationClick = useCallback((notification) => {
    // Mark as read locally
    if (!notification.read) {
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      // Mark as read on backend (fire-and-forget)
      if (typeof notification.id === 'string' && notification.id.length > 10) {
        api.patch(`/profile/notifications/${notification.id}/read`).catch(() => {});
      }
    }

    const type = notification.type;
    const data = notification.data || {};
    const symptomId = getSymptomId(data);
    const isDoctor = user?.role === 'DOCTOR';
    const isPatient = user?.role === 'USER';

    // ── Route map ─────────────────────────────────────────────
    // newSymptom / new_patient_report → doctor sees symptom detail
    if (type === 'newSymptom' || type === 'new_patient_report') {
      if (isDoctor && symptomId) navigate(`/symptom/${symptomId}`);
      else if (isDoctor) navigate('/health-log');
      else navigate('/my-health');
    }
    // newResponse / doctor_response → patient sees symptom detail
    else if (type === 'newResponse' || type === 'doctor_response') {
      if (symptomId) navigate(`/symptom/${symptomId}`);
      else if (isPatient) navigate('/my-health');
      else navigate('/health-log');
    }
    // patientReply / patient_reply → doctor sees symptom detail
    else if (type === 'patientReply' || type === 'patient_reply') {
      if (symptomId) navigate(`/symptom/${symptomId}`);
      else navigate('/health-log');
    }
    // newChatMessage / chat_message → open the chat room
    else if (type === 'newChatMessage' || type === 'chat_message') {
      if (data.symptomId && data.roomId) {
        navigate(`/chat/${data.symptomId}?roomId=${encodeURIComponent(data.roomId)}`);
      } else if (data.symptomId) {
        navigate(`/chat/${data.symptomId}`);
      } else {
        navigate('/messages');
      }
    }
    // symptomStatusUpdate / symptom_status_update → symptom detail or My Health
    else if (type === 'symptomStatusUpdate' || type === 'symptom_status_update') {
      if (symptomId) navigate(`/symptom/${symptomId}`);
      else if (isPatient) navigate('/my-health');
      else navigate('/health-log');
    }
    // newCareRequest / care_request_received → patient goes to care requests
    else if (type === 'newCareRequest' || type === 'care_request_received') {
      navigate('/care-requests');
    }
    // record_updated_by_doctor → patient sees their updated health record
    else if (type === 'record_updated_by_doctor') {
      navigate('/my-health');
    }
    // consult_requested → invited doctor goes directly to the symptom to review
    else if (type === 'consult_requested') {
      if (symptomId) navigate(`/symptom/${symptomId}`);
      else navigate('/doctor-dashboard');
    }
    // consult_accepted / consult_declined → requesting doctor goes to the symptom
    else if (type === 'consult_accepted' || type === 'consult_declined') {
      if (symptomId) navigate(`/symptom/${symptomId}`);
      else navigate('/doctor-dashboard');
    }
    // actionDelegated → doctor goes to their dashboard to see delegated actions
    else if (type === 'actionDelegated') {
      navigate('/doctor-dashboard');
    }
    // care_action_executed → patient goes to their dashboard to see the update
    else if (type === 'care_action_executed') {
      navigate('/dashboard');
    }
    // appointment_scheduled → patient goes to the care event detail (or dashboard)
    else if (type === 'appointment_scheduled') {
      const eventId = data.careEventId || data.eventId || data._id;
      if (eventId) navigate(`/care-event/${eventId}`);
      else navigate('/dashboard');
    }
    // careEvent / care_event → care event detail
    else if (type === 'careEvent' || type === 'care_event') {
      const eventId = data.eventId || data.careEventId || data._id;
      if (eventId) navigate(`/care-event/${eventId}`);
      else if (isDoctor) navigate('/doctor-dashboard');
      else navigate('/dashboard');
    }
    // Fallback
    else {
      if (isDoctor) navigate('/doctor-dashboard');
      else if (isPatient) navigate('/dashboard');
      else navigate('/admin');
    }

    setIsOpen(false);
  }, [navigate, user]);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Get all unread counts grouped by roomId
  const getAllUnreadCounts = useCallback(() => {
    const counts = {};
    notifications.forEach(n => {
      if (n.data?.roomId && !n.read) {
        counts[n.data.roomId] = (counts[n.data.roomId] || 0) + 1;
      }
    });
    return counts;
  }, [notifications]);

  // Clear notifications for a specific room (e.g., when opening a private chat with a specific doctor)
  const clearNotificationsByRoomId = useCallback((roomId) => {
    setNotifications(prev => {
      // Check if this room actually has notifications
      const hasNotificationsForRoom = prev.some(n => n.data?.roomId === roomId && !n.read);
      if (!hasNotificationsForRoom) return prev;

      const filtered = prev.filter(n => n.data?.roomId !== roomId);

      // Update count immediately in this render cycle
      setUnreadCount(filtered.filter(n => !n.read).length);

      // Calculate new counts and emit
      const newCounts = {};
      filtered.forEach(n => {
        if (n.data?.roomId && !n.read) {
          newCounts[n.data.roomId] = (newCounts[n.data.roomId] || 0) + 1;
        }
      });

      setTimeout(() => {
        webSocketEventBus.emit('notificationsChanged', newCounts);
      }, 0);

      return filtered;
    });
  }, []);

  // Expose functions through event bus
  // Use refs to track clearing operations across renders (survives StrictMode)
  const clearingRoomsRef = useRef(new Set());
  const lastClearTimeRef = useRef({});
  
  useEffect(() => {
    const handleClearNotifications = (roomId) => {
      const now = Date.now();
      const lastClear = lastClearTimeRef.current[roomId] || 0;
      const timeSinceLastClear = now - lastClear;

      // Prevent duplicate clears within 100ms OR if already in progress
      if (clearingRoomsRef.current.has(roomId) || timeSinceLastClear < 100) return;

      clearingRoomsRef.current.add(roomId);
      lastClearTimeRef.current[roomId] = now;
      
      clearNotificationsByRoomId(roomId);
      
      // Remove from set after delay
      setTimeout(() => {
        clearingRoomsRef.current.delete(roomId);
      }, 100);
    };
    
    const handleGetAllUnreadCounts = () => {
      return getAllUnreadCounts();
    };
    
    const handleRequestCounts = () => {
      const counts = getAllUnreadCounts();
      webSocketEventBus.emit('notificationsChanged', counts);
      // Also emit unreadCounts with perSymptom for MessagesPage
      const current = notificationsRef.current;
      const unread = current.filter(n => !n.read);
      const perSymptom = {};
      for (const n of unread) {
        const sid = n.data?.symptomId;
        if (sid) perSymptom[sid] = (perSymptom[sid] || 0) + 1;
      }
      webSocketEventBus.emit('unreadCounts', {
        total: unread.length,
        messages: unread.filter(n => n.type === 'newChatMessage' || n.type === 'chat_message').length,
        perSymptom
      });
    };
    
    // Clear all notifications for a given symptomId (used when user enters a chat)
    const handleClearSymptomNotifications = (symptomId) => {
      if (!symptomId) return;
      setNotifications(prev => {
        let changed = false;
        const updated = prev.map(n => {
          if (!n.read && n.data?.symptomId === symptomId) {
            changed = true;
            // Mark as read on backend (fire-and-forget)
            if (typeof n.id === 'string' && n.id.length > 10) {
              api.patch(`/profile/notifications/${n.id}/read`).catch(() => {});
            }
            return { ...n, read: true };
          }
          return n;
        });
        if (!changed) return prev;
        setUnreadCount(updated.filter(n => !n.read).length);
        return updated;
      });
    };

    webSocketEventBus.on('clearNotificationsByRoomId', handleClearNotifications);
    webSocketEventBus.on('clearSymptomNotifications', handleClearSymptomNotifications);
    webSocketEventBus.on('getAllUnreadCounts', handleGetAllUnreadCounts);
    webSocketEventBus.on('requestNotificationCounts', handleRequestCounts);
    
    // Emit initial counts
    webSocketEventBus.emit('notificationsChanged', getAllUnreadCounts());
    
    return () => {
      webSocketEventBus.off('clearNotificationsByRoomId', handleClearNotifications);
      webSocketEventBus.off('clearSymptomNotifications', handleClearSymptomNotifications);
      webSocketEventBus.off('getAllUnreadCounts', handleGetAllUnreadCounts);
      webSocketEventBus.off('requestNotificationCounts', handleRequestCounts);
    };
  }, [clearNotificationsByRoomId, getAllUnreadCounts]);

  // Persist notifications to localStorage and emit changes
  useEffect(() => {
    try {
      // Keep only the latest 50 notifications to avoid bloating localStorage
      const toSave = notifications.slice(0, 50);
      localStorage.setItem('symptoconnect_notifications', JSON.stringify(toSave));
    } catch (e) {
      console.warn('Failed to persist notifications:', e);
    }
    webSocketEventBus.emit('notificationsChanged', getAllUnreadCounts());

    // Emit total unread count for sidebar/bottom-nav badges
    const unread = notifications.filter(n => !n.read);
    const totalUnread = unread.length;
    const messageUnread = unread.filter(n => n.type === 'newChatMessage' || n.type === 'chat_message').length;

    // Build per-symptom unread counts for MessagesPage
    const perSymptom = {};
    for (const n of unread) {
      const sid = n.data?.symptomId;
      if (sid) perSymptom[sid] = (perSymptom[sid] || 0) + 1;
    }

    webSocketEventBus.emit('unreadCounts', { total: totalUnread, messages: messageUnread, perSymptom });
  }, [notifications, getAllUnreadCounts]);

  // Load persisted notifications from API on mount
  useEffect(() => {
    if (!user) return;
    loadPersistedNotifications();
  }, [user, loadPersistedNotifications]);

  // WebSocket setup and cleanup
  useEffect(() => {
    if (!user) return;

    // AuthContext already manages socket connection, just get the existing socket
    const socket = getSocket();
    if (!socket) return;

    // Store handler references for proper cleanup
    const handlers = {
      newResponse: handleNewResponse,
      newSymptom: handleNewSymptom,
      patientReply: handlePatientReply,
      newChatMessage: handleNewChatMessage,
      symptomStatusUpdate: handleSymptomStatusUpdate,
      newCareRequest: handleNewCareRequest,
      actionDelegated: handleActionDelegated,
      appointmentScheduled: handleAppointmentScheduled,
      careActionExecuted: handleCareActionExecuted,
    };

    // Register listeners
    socket.on('newResponse', handlers.newResponse);
    socket.on('newSymptom', handlers.newSymptom);
    socket.on('patientReply', handlers.patientReply);
    socket.on('newChatMessage', handlers.newChatMessage);
    socket.on('symptomStatusUpdate', handlers.symptomStatusUpdate);
    socket.on('newCareRequest', handlers.newCareRequest);
    socket.on('actionDelegated', handlers.actionDelegated);
    socket.on('appointmentScheduled', handlers.appointmentScheduled);
    socket.on('careActionExecuted', handlers.careActionExecuted);

    // Cleanup function
    return () => {
      const currentSocket = getSocket();
      if (currentSocket && typeof currentSocket.off === 'function') {
        currentSocket.off('newResponse', handlers.newResponse);
        currentSocket.off('newSymptom', handlers.newSymptom);
        currentSocket.off('patientReply', handlers.patientReply);
        currentSocket.off('newChatMessage', handlers.newChatMessage);
        currentSocket.off('symptomStatusUpdate', handlers.symptomStatusUpdate);
        currentSocket.off('newCareRequest', handlers.newCareRequest);
        currentSocket.off('actionDelegated', handlers.actionDelegated);
        currentSocket.off('appointmentScheduled', handlers.appointmentScheduled);
        currentSocket.off('careActionExecuted', handlers.careActionExecuted);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleNewResponse, handleNewSymptom, handlePatientReply, handleNewChatMessage, handleSymptomStatusUpdate, handleNewCareRequest, handleActionDelegated, handleAppointmentScheduled, handleCareActionExecuted]); // Re-run when handlers change

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Request notification permissions
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Resolve a type label for the Care-Bubble chip
  const getTypeChip = (type) => {
    const map = {
      newSymptom: { label: 'New Report', bg: '#d6e3ff', color: '#00478d' },
      new_patient_report: { label: 'New Report', bg: '#d6e3ff', color: '#00478d' },
      newResponse: { label: 'Response', bg: '#97f2f3', color: '#007072' },
      doctor_response: { label: 'Response', bg: '#97f2f3', color: '#007072' },
      patientReply: { label: 'Reply', bg: '#97f2f3', color: '#007072' },
      newChatMessage: { label: 'Message', bg: '#97f2f3', color: '#007072' },
      newCareRequest: { label: 'Care Request', bg: '#ffdea8', color: '#5f4300' },
      care_request_received: { label: 'Care Request', bg: '#ffdea8', color: '#5f4300' },
      record_updated_by_doctor: { label: 'Record Update', bg: '#d6e3ff', color: '#00478d' },
      symptomStatusUpdate: { label: 'Status', bg: '#e2e2e6', color: '#424752' },
      actionDelegated: { label: 'Action', bg: '#ffdea8', color: '#5f4300' },
      appointment_scheduled: { label: 'Appointment', bg: '#d6e3ff', color: '#00478d' },
      care_action_executed: { label: 'Care Action', bg: '#d9f4e8', color: '#1a6b40' },
    };
    return map[type] || { label: 'Notification', bg: '#e2e2e6', color: '#424752' };
  };

  const formatTime = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    if (h < 24) return `${h}h ago`;
    return `${d}d ago`;
  };

  // UI Render
  return (
    <div className="relative">
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full transition-colors focus:outline-none focus-visible:ring-2"
        style={{ color: '#424752' }}
        title="Notifications"
        aria-label="Notifications"
        onMouseEnter={e => e.currentTarget.style.background = '#f3f3f7'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 text-white text-[10px] font-extrabold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1" style={{ background: '#ba1a1a' }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Dropdown */}
          <div
            className="absolute right-0 top-full mt-2 w-[340px] rounded-3xl z-50 overflow-hidden"
            style={{ background: '#ffffff', boxShadow: '0px 12px 32px rgba(25,28,30,0.10)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div>
                <h3 className="font-headline font-bold text-sm" style={{ color: '#191c1e' }}>Notifications</h3>
                {unreadCount > 0 && (
                  <p className="text-xs font-semibold mt-0.5" style={{ color: '#727783' }}>{unreadCount} unread</p>
                )}
              </div>
              {notifications.length > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); clearAllNotifications(); }}
                  className="text-xs font-bold transition-colors px-3 py-1.5 rounded-full"
                  style={{ background: '#f3f3f7', color: '#424752' }}
                >
                  Clear all
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-[400px] overflow-y-auto pb-2">
              {notifications.length === 0 ? (
                <div className="px-5 py-10 text-center">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: '#f3f3f7' }}>
                    <svg className="w-5 h-5" fill="none" stroke="#727783" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold" style={{ color: '#424752' }}>You're all caught up</p>
                  <p className="text-xs mt-1" style={{ color: '#727783' }}>No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => {
                  const chip = getTypeChip(notification.type);
                  return (
                    <div
                      key={notification.id}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(false); handleNotificationClick(notification); }}
                      className="mx-2 mb-1 rounded-2xl px-4 py-3.5 cursor-pointer transition-all"
                      style={{ background: !notification.read ? '#f3f8ff' : 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f3f3f7'}
                      onMouseLeave={e => e.currentTarget.style.background = !notification.read ? '#f3f8ff' : 'transparent'}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotificationClick(notification); } }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Care-Bubble type chip */}
                          <div className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ background: chip.bg, color: chip.color }}>
                            {chip.label}
                          </div>
                          <p className="text-sm font-bold leading-snug mb-0.5" style={{ color: '#191c1e' }}>
                            {notification.title}
                          </p>
                          <p className="text-xs leading-relaxed line-clamp-2" style={{ color: '#424752' }}>
                            {notification.message}
                          </p>
                          <p className="text-[10px] font-semibold mt-1.5" style={{ color: '#727783' }}>
                            {formatTime(notification.timestamp)}
                          </p>
                        </div>
                        {!notification.read && (
                          <div className="w-2 h-2 rounded-full shrink-0 mt-1" style={{ background: '#00478d' }} />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationSystem;